// Shared shuffle rules for the RULER editor.
//
// This module is the SINGLE source of truth for the shuffle feature and is used by BOTH:
//  - the frontend (client-side pure-shuffle fallback in App.js when the worker is unreachable)
//  - the Cloudflare Worker (worker/src/shuffle.js — Colormind API on top of these rules)
//
// Keep it dependency-light and pure (no fetch / DOM / React): it must run in the
// browser AND in the Workers runtime. `npm test` covers it via src/shuffleRules.test.js.

import { colorList, buttonColorList, defaultBlackConfigLight } from './ConfigCode.js';

// Product palette hex values — mirrors src/ColorSelect.js `colorMap` (black uses the
// render hex #303030 from HoodColor.css rather than the swatch #212121).
export const COLOR_HEX = {
  'red': '#E90924',
  'orange': '#FF6512',
  'yellow': '#FBEF12',
  'green': '#76F720',
  'tiffany': '#0BD4AC',
  'light-blue': '#48C3E8',
  'blue': '#2C33C7',
  'brown': '#4F321F',
  'pupple': '#863CB5',
  'pink': '#F9CDDA',
  'white': '#FFFFFF',
  'gray': '#7D7D7D',
  'black': '#303030',
  'texture-black': '#333333',
  'texture-1': '#8C7D5C',
  'texture-2': '#B4B4B6',
  'metal': '#666666',
  'dark-green': '#205C50',
  'dark-blue': '#253856',
  'dark-gray': '#575757',
  'rose-red': '#E34496',
  'apricot': '#EDD5AB',
  'lake-blue': '#0092DB',
  'peach-red': '#FF6B75',
  'grass-green': '#36AD49',
  'dark-chocolate': '#403528',
};

// Textures (压纹/迷彩) and metal are intentionally excluded from shuffles:
// they cost extra (+20R for texture-black) and a random camo/金属 assignment looks
// broken. Users can still pick them manually in the drawer.
export const SHUFFLE_EXCLUDED = new Set(['metal', 'texture-black', 'texture-1', 'texture-2']);

export const SOLID_COLORS = colorList.filter((c) => !SHUFFLE_EXCLUDED.has(c));
export const BUTTON_SOLIDS = buttonColorList.filter((c) => !SHUFFLE_EXCLUDED.has(c));

export const BUTTON_PARTS = new Set(['bridge-button', 'muzzle-button', 'muzzle-button-b', 'ear-button']);

// Mirrors the per-part ignore lists in App.js (ColorSelect ignore={...}).
// ColorSelect already ignores metal + textures via SHUFFLE_EXCLUDED, so only the
// *extra* App.js exclusions are listed here.
const EXTRA_EXCLUDES = {
  'hood-base-color': new Set(['tiffany', 'green', 'lake-blue', 'peach-red', 'grass-green', 'dark-chocolate']),
  'crown-color': new Set(['peach-red', 'grass-green', 'dark-chocolate']),
  'ear-a-color': new Set(['dark-gray']),
  'ear-b-color': new Set(['dark-gray']),
  'ear-c-color': new Set(['dark-gray']),
};

// Parts visible/editable in each editor mode (mirrors the drawer in App.js).
const HOOD_PARTS = [
  'hood-base-color', 'crown-color', 'brow-color', 'bridge-color', 'nose-color',
  'muzzle-color', 'muzzle-lip-color', 'chin-color', 'muzzle-a-color', 'muzzle-b-color',
  'whiskers-color', 'nose-patch-color', 'ear-a-color', 'ear-b-color', 'ear-c-color',
  'bridge-button', 'muzzle-button', 'muzzle-button-b',
];

const MUZZLE_PARTS = [
  'bridge-color', 'nose-color', 'muzzle-color', 'muzzle-lip-color',
  'muzzle-a-color', 'muzzle-b-color', 'whiskers-color', 'nose-patch-color',
  'ear-a-color', 'ear-b-color', 'ear-c-color',
  'ear-button', 'muzzle-button', 'muzzle-button-b',
];

export function partsForMode(mode) {
  return mode === 'muzzle' ? MUZZLE_PARTS : HOOD_PARTS;
}

// Big surface parts get a HIGH probability of black — black is the brand's main
// color (most real orders are black-based). See BLACK_MAIN_WEIGHT.
export const MAIN_PARTS = {
  hood: new Set(['hood-base-color', 'crown-color', 'muzzle-color', 'chin-color']),
  muzzle: new Set(['muzzle-color', 'bridge-color']),
};

// Probability that a main part lands on black (0.5 = "big random rate").
export const BLACK_MAIN_WEIGHT = 0.5;

export function wantsBlackMain(partKey, mode) {
  const set = MAIN_PARTS[mode];
  return !!set && set.has(partKey) && Math.random() < BLACK_MAIN_WEIGHT;
}

export function allowedColorsFor(partKey) {
  if (BUTTON_PARTS.has(partKey)) return BUTTON_SOLIDS;
  const extra = EXTRA_EXCLUDES[partKey];
  if (!extra) return SOLID_COLORS;
  return SOLID_COLORS.filter((c) => !extra.has(c));
}

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ---- Saturation awareness (低饱和度 fix) ----
// Colormind returns desaturated palettes (measured ~0.31 avg saturation), so
// nearest-color snapping lands on muted product colors (gray/brown/dark tones).
// Colors are therefore weighted by perceived saturation: vibrant colors are picked
// ~3x more often than neutrals, so shuffles stay lively.
function rgbToSaturation(r, g, b) {
  const max = Math.max(r, g, b) / 255;
  const min = Math.min(r, g, b) / 255;
  const l = (max + min) / 2;
  if (max === min) return 0;
  const d = max - min;
  return l > 0.5 ? d / (2 - max - min) : d / (max + min);
}

const COLOR_SAT = {};
for (const [key, hex] of Object.entries(COLOR_HEX)) {
  const h = hex.replace('#', '');
  COLOR_SAT[key] = rgbToSaturation(parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16));
}

// Pick weight per color. Black/white stay relevant (contrast + brand black) at 0.55;
// gray-family colors are the least preferred (0.35); vibrant colors reach ~1.1.
export function colorWeight(colorKey) {
  if (colorKey === 'black' || colorKey === 'white') return 0.55;
  return 0.35 + 0.75 * (COLOR_SAT[colorKey] || 0);
}

// Saturation-weighted random pick from an allowed list.
export function pickWeightedColor(allowed) {
  const weights = allowed.map(colorWeight);
  const total = weights.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  for (let i = 0; i < allowed.length; i++) {
    r -= weights[i];
    if (r <= 0) return allowed[i];
  }
  return allowed[allowed.length - 1];
}

// Shuffle pick for a single part: black 50% on main parts, saturation-weighted
// (vibrant-heavy) everywhere else.
export function pickShuffleColor(partKey, mode) {
  const allowed = allowedColorsFor(partKey);
  if (wantsBlackMain(partKey, mode) && allowed.includes('black')) return 'black';
  return pickWeightedColor(allowed);
}

// Styles follow the same dependency rules as the App.js pickers:
//  - muzzle-style: default/fur/style1/style2, with a chance of the added-* cheek variant
//  - muzzle-assoicate: default/mustache/fur
//  - eyebrow-style: default/dou/frame
//  - spikes-style: only meaningful on the 三孔 (default) associate — forced off otherwise
export function shuffleStyles(config) {
  const newConfig = { ...config };
  const base = pick(['default', 'fur', 'style1', 'style2']);
  newConfig['muzzle-style'] =
    (base === 'default' || base === 'fur') && Math.random() < 0.35 ? 'added-' + base : base;
  newConfig['muzzle-assoicate'] = pick(['default', 'mustache', 'fur']);
  newConfig['eyebrow-style'] = pick(['default', 'dou', 'frame']);
  newConfig['spikes-style'] =
    newConfig['muzzle-assoicate'] === 'default' ? pick(['default', 'spikes1', 'spikes2']) : 'default';
  return newConfig;
}

// Mirror the App.js drawer: black-type-light (光皮/哑光 material) is only exposed in
// hood mode, so it is only shuffled there. ear-hole / glass-hole (两个孔) are
// functional options and are NEVER shuffled — they stay as the user chose.
export function shuffleChecks(config, mode) {
  const newConfig = { ...config };
  newConfig['nose-patch'] = Math.random() < 0.5;
  if (mode !== 'muzzle') {
    newConfig['black-type-light'] = Math.random() < 0.5;
  }
  return newConfig;
}

// Keep the user's glossy/matte (光皮/哑光) preference for parts that stay black;
// newly-black parts default to the current black-type-light toggle.
function applyBlackDefaults(newConfig, parts, blackConfig, blackTypeLight) {
  const newBlack = blackConfig && typeof blackConfig === 'object' ? { ...blackConfig } : {};
  for (const part of parts) {
    if (newConfig[part] === 'black') {
      newBlack[part] = typeof newBlack[part] === 'boolean' ? newBlack[part] : !!blackTypeLight;
    }
  }
  return newBlack;
}

// All color parts (same keys as ConfigCode's defaultBlackConfigLight/NonLight).
const ALL_COLOR_PARTS = Object.keys(defaultBlackConfigLight);

// Keep the user's glossy/matte (光皮/哑光) preference for parts that stay black;
// newly-black parts default to the current black-type-light toggle. If the global
// toggle flipped, mirror the drawer behaviour (handleCheckChange resets every part
// to the new default).
export function resolveBlackConfig(newConfig, parts, blackConfig, prevBlackTypeLight) {
  if (newConfig['black-type-light'] !== prevBlackTypeLight) {
    const value = !!newConfig['black-type-light'];
    return Object.fromEntries(ALL_COLOR_PARTS.map((p) => [p, value]));
  }
  return applyBlackDefaults(newConfig, parts, blackConfig, newConfig['black-type-light']);
}

// Pure random shuffle — also the backend's fallback when Colormind is down.
export function pureShuffleConfig({ config, blackConfig, blackTypeLight, mode }) {
  const parts = partsForMode(mode);
  const newConfig = { ...config };
  parts.forEach((part) => {
    newConfig[part] = pickShuffleColor(part, mode);
  });
  const styled = shuffleChecks(shuffleStyles(newConfig), mode);
  return { config: styled, blackConfig: resolveBlackConfig(styled, parts, blackConfig, blackTypeLight) };
}
